import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import "../styles/books.css";

/* ─────────────────────────────────────────────
   BOOK DATA — edit this array only.
   • cover: file in /public/images/books/ (aim for 2:3 ratio, e.g. 600×900,
     compressed .jpg or .webp)
   • url:   the Amazon product link
   • blurb: 1–2 sentences (keep under ~220 characters)
   • These are PLACEHOLDERS — replace with Stan's real titles.
───────────────────────────────────────────── */
const BOOKS = [
  {
    id: "book-1",
    title: "Synergistic Intelligence: Personal Edition",
    subtitle:
      "What if the real problem isn’t a lack of intelligence but how we use it?",
    year: "2026",
    blurb:
      "Despite living in the most advanced era in human history, our biggest challenges remain unsolved.",
    cover:
      process.env.PUBLIC_URL + "/images/books/synergistic_intelligence.jpg",
    url: "https://a.co/d/0iU1IiA3",
  },
  {
    id: "book-2",
    title: "Synergistic Intelligence: For Organizations",
    subtitle: "Most organizations are full of capable people who each see only a piece of the picture.",
    year: "2026",
    blurb:
      "Synergistic Intelligence by Stan Cross applies the ideas of his earlier work to teams, leaders, and the systems they work within.",
    cover:
      process.env.PUBLIC_URL + "/images/books/synergistic_intelligence_for_orgs.jpg",
    url: "https://a.co/d/00Psy62K",
  },
  {
    id: "book-3",
    title: "The Intelligence Was Never Ours",
    subtitle: "There is a quiet assumption underneath modern life: that intelligence is something we own.",
    year: "2026",
    blurb:
      "We prove it through credentials. We defend it through certainty. We build entire systems around those who appear to 'know.' This book dismantles that assumption.",
    cover: process.env.PUBLIC_URL + "/images/books/the_intelligence.jpg",
    url: "https://a.co/d/0gl2wX9w",
  },
  {
    id: "book-4",
    title: "Flow: Identifying with It Changes Everything",
    subtitle: "What if flow is not something you occasionally experience, but something you are?",
    year: "2026",
    blurb:
      "What if flow is not merely a state of consciousness, but a fundamental way of understanding ourselves and the world?",
    cover: process.env.PUBLIC_URL + "/images/books/flow.jpg",
    url: "https://a.co/d/0bIaxVWp",
  },
  {
    id: "book-5",
    title: "The Flow Identity Journal",
    subtitle: "The Flow Journey changed you. But transformation isn't a destination, it's a practice.",
    year: "2026",
    blurb:
      "This journal is for anyone who walked the Flow Journey and discovered something true about themselves. And it's for anyone, anywhere, who's ready to do that work now.",
    cover: process.env.PUBLIC_URL + "/images/books/flow_identity_journal.jpg",
    url: "https://a.co/d/0bNS1Rw5",
  },
  {
    id: "book-6",
    title: "Vision: The Most Powerful Force in the World",
    subtitle: "What we believe people are decides what we expect from them.",
    year: "2026",
    blurb: "VISION traces that inheritance from Augustine to the annual review, then asks the harder question: what would we build if we believed something else?",
    cover: process.env.PUBLIC_URL + "/images/books/vision.jpg",
    url: "https://a.co/d/01g6DDiR",
  },
];

// Shared fade-up variant (matches the other pages)
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

/* Cover with graceful fallback if the image is missing or fails to load */
function BookCover({ title, src }) {
  const [failed, setFailed] = useState(false);

  if (failed || !src) {
    return (
      <div
        className="book-cover book-cover--fallback"
        role="img"
        aria-label={`Cover of ${title}`}
      >
        <span>{title}</span>
      </div>
    );
  }

  return (
    <img
      className="book-cover"
      src={src}
      alt={`Cover of ${title}`}
      width="400"
      height="600"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function BookCard({ book, reduceMotion }) {
  const { title, subtitle, year, blurb, cover, url } = book;

  return (
    <motion.li
      className="book-card"
      initial={reduceMotion ? false : { opacity: 0, y: 30 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Cover is a mouse/touch convenience; keyboard + screen reader users get the button below */}
      <a
        className="book-cover-link"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={-1}
        aria-hidden="true"
      >
        <BookCover title={title} src={cover} />
      </a>

      <div className="book-info">
        <div className="book-meta">
          <h3 className="book-title">{title}</h3>
          {subtitle && <p className="book-subtitle">{subtitle}</p>}
          {year && <p className="book-year">{year}</p>}
        </div>
        <p className="book-blurb">{blurb}</p>
        <a
          className="book-btn"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          View on Amazon
          <span className="book-btn-arrow" aria-hidden="true">
            ↗
          </span>
          <span className="sr-only"> {title} (opens in a new tab)</span>
        </a>
      </div>
    </motion.li>
  );
}

export default function Books() {
  const reduceMotion = useReducedMotion();

  return (
    <>
      {/* ── HERO ── */}
      <motion.section
        className="books-hero"
        initial={reduceMotion ? false : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <p className="section-label">Books</p>
        <h1>
          Writing from
          <br />
          <em>the other side of the question.</em>
        </h1>
        <p className="books-hero-lead">
          Every book here was written by Stan Cross, co-founder of Synergistic
          Intelligence. They are the long-form version of the work: the ideas
          behind the Flow Journey, in his own words.
        </p>
      </motion.section>

      {/* ── LIBRARY ── */}
      <motion.section
        className="books-library"
        aria-labelledby="books-heading"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={reduceMotion ? undefined : fadeUp}
      >

        <ul className="books-collection">
          {BOOKS.map((book) => (
            <BookCard key={book.id} book={book} reduceMotion={reduceMotion} />
          ))}
        </ul>

        <p className="books-note">
          Books are sold through Amazon. Links open in a new tab.
        </p>
      </motion.section>

      {/* ── CTA BAND ── */}
      <section className="books-cta">
        <h2>Prefer a conversation to a book?</h2>
        <p>
          The Flow Identity Assessment takes ten minutes, costs nothing, and
          meets you exactly where you are.
        </p>
        <Link to="/assessment" className="books-cta-btn">
          Take the Flow Identity Assessment →
        </Link>
      </section>
    </>
  );
}